import { FilterAlt } from "@mui/icons-material";
import {
    Checkbox,
    IconButton,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Divider,
    Typography,
    TextField,
} from "@mui/material";
import { useMemo, useState } from "react";

const MAX_RESULTS = 1000;

const ColumnFilter = ({ column, rows, onFilterChange, selectedValues, normalizeValue, displayValue, bt }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [search, setSearch] = useState("");

    const handleClick = (event) => {
        event.preventDefault()
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearch("");
    };

    const rawValues = useMemo(() => {
        const values = new Set();
        for (const row of rows) {
            const val = row[column.field];
            if (val !== undefined && val !== null) {
                values.add(val);
            }
        }
        return Array.from(values);
    }, [rows, column.field]);

    const allValues = useMemo(() => {
        return [...new Set(rawValues?.map(normalizeValue))].sort((a, b) =>
            displayValue(a).localeCompare(displayValue(b))
        );
    }, [rawValues]);

    const filteredValues = useMemo(() => {
        return allValues.filter((val) => displayValue(val).toLowerCase().includes(search.toLowerCase())).slice(0, MAX_RESULTS);
    }, [allValues, search]);

    const isAllFilteredSelected = filteredValues.every((val) => selectedValues.includes(val));

    const toggleAll = () => {
        const newValues = isAllFilteredSelected
            ? selectedValues.filter((v) => !filteredValues.includes(v))
            : [...new Set([...selectedValues, ...filteredValues])];

        onFilterChange(column.field, newValues);
    };

    const handleToggle = (value) => {
        const newValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];
        onFilterChange(column.field, newValues);
    };

    const handleClear = () => {
        onFilterChange(column.field, []);
        handleClose();
    };

    return (
        <>
            <IconButton
                onClick={handleClick}
                onContextMenu={handleClick}
                size="small"
            >
                <FilterAlt
                    sx={{ color: selectedValues.length > 0 ? "warning.main" : "default" }}
                    fontSize="small"
                />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Stack spacing={1} minWidth={250} maxWidth={300} maxHeight={300} overflow='hidden' padding={1}>
                    <TextField
                        size="small"
                        placeholder="חיפוש..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <Stack direction="row" justifyContent="space-between">
                        <Typography
                            variant="body2"
                            color="primary"
                            onClick={toggleAll}
                            sx={{ cursor: "pointer" }}
                        >
                            {isAllFilteredSelected ? bt("DESELECT_ALL") : bt("SELECT_ALL")}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="error.text"
                            onClick={handleClear}
                            sx={{ cursor: "pointer" }}
                        >
                            נקה סינון
                        </Typography>
                    </Stack>
                    <Divider />

                    <Stack sx={{ overflowY: "auto", overflowX: 'hidden' }} >
                        {filteredValues?.map((value) =>
                            value !== null && value !== undefined ? (
                                <MenuItem sx={{ p: 0 }} key={value} onClick={() => handleToggle(value)}>
                                    <Checkbox checked={selectedValues.includes(value)} />
                                    <ListItemText primary={displayValue(value)} />
                                </MenuItem>
                            ) : null
                        )}
                        {filteredValues.length === 0 && (
                            <Typography variant="caption" textAlign="center" p={1} color="text.secondary">
                                אין תוצאות
                            </Typography>
                        )}
                        {allValues.length > MAX_RESULTS && (
                            <Typography variant="caption" textAlign="center" p={1} color="text.secondary">
                                מציג {MAX_RESULTS} מתוך {allValues.length} ערכים
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </Menu>
        </>
    );
};

export default ColumnFilter;
